<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">visibility</span>
      <p>
        Registra el examen oftalmológico completo del paciente: síntomas,
        agudeza visual, refracción con receta óptica, biomicroscopía y
        fondo de ojo con tonometría.
      </p>
    </div>

    <!-- Modos de trabajo -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">toggle_on</span>
        Modos de trabajo
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in modos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pestañas -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">tab</span>
        Pestañas del examen
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="tab in tabs" :key="tab.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ tab.icon }}</span>
          <div>
            <p class="help-card-title">{{ tab.titulo }}</p>
            <p class="help-card-desc">{{ tab.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Agudeza visual -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">center_focus_strong</span>
        Agudeza visual — campos
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in camposAgudeza" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Refracción -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lens_blur</span>
        Refracción y receta óptica — campos
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in camposRefraccion" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tonometría -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">speed</span>
        Tonometría (PIO)
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-bottom:0.5rem">
        La presión intraocular normal es de <strong>10 – 21 mmHg</strong>.
        El sistema marca automáticamente en <span style="color:#dc3545">rojo</span>
        los valores superiores a 21 mmHg con la etiqueta <strong>⚠ Alta</strong>.
        Registra el método de medición y la hora de toma para mayor precisión diagnóstica.
      </p>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Restricción -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lock</span>
        Restricciones por estado de la atención
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">PENDIENTE</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">PROCESO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        El Modo Edición y los botones Guardar y Limpiar solo están habilitados
        cuando la atención está en estado <strong>PENDIENTE</strong> o
        <strong>PROCESO</strong>. En otros estados el componente abre
        directamente en <strong>Modo Consulta</strong>.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Al ingresar el numerador y denominador de Snellen, el valor
        <strong>Decimal</strong> se calcula automáticamente. Puedes agregar
        múltiples registros de agudeza, refracción, biomicroscopía y fondo
        de ojo para documentar ambos ojos o distintas condiciones de medición.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaOftalmologia",
  data() {
    return {
      modos: [
        {
          icon: "brush",
          titulo: "Modo Edición",
          desc:   "Permite ingresar y modificar todos los datos del examen oftalmológico. Solo disponible en atenciones PENDIENTE o PROCESO.",
        },
        {
          icon: "pageview",
          titulo: "Modo Consulta",
          desc:   "Solo lectura. Permite revisar los datos registrados sin posibilidad de modificarlos. Disponible en cualquier estado.",
        },
      ],
      tabs: [
        {
          icon: "medical_information",
          titulo: "General",
          desc:   "Tipo de consulta, motivo, antecedentes oftalmológicos y síntomas del paciente. Los síntomas activos se resumen en etiquetas de color al pie de la sección.",
        },
        {
          icon: "center_focus_strong",
          titulo: "Agudeza visual",
          desc:   "Medición de la agudeza visual por ojo y condición (con o sin corrección) en escala de Snellen, decimal y LogMAR. Se pueden agregar múltiples registros.",
        },
        {
          icon: "lens_blur",
          titulo: "Refracción",
          desc:   "Datos de refracción subjetiva, objetiva o lensometría para lejos y cerca: esfera, cilindro y eje. Incluye la receta óptica con distancia pupilar, tipo de lente, material y filtro.",
        },
        {
          icon: "biotech",
          titulo: "Biomicroscopía",
          desc:   "Hallazgos de la lámpara de hendidura: párpados, pestañas, conjuntiva, córnea, cámara anterior, iris, cristalino y vítreo anterior.",
        },
        {
          icon: "ophthalmology",
          titulo: "Fondo / PIO",
          desc:   "Tonometría (presión intraocular) con método y hora de toma, y fondo de ojo: papila, C/D ratio, mácula, vasos retinianos, retina periférica y vítreo.",
        },
      ],
      camposAgudeza: [
        { icon: "remove_red_eye", titulo: "Ojo (OD / OI)",       desc: "Indica si la medición corresponde al ojo derecho (OD) o izquierdo (OI)." },
        { icon: "visibility",     titulo: "Condición",            desc: "Indica si la medición es sin corrección (visión natural) o con corrección (con lentes)." },
        { icon: "123",            titulo: "Snellen num. / den.",  desc: "Numerador y denominador de la escala de Snellen (p. ej. 20/200). Al ingresar ambos valores el decimal se calcula automáticamente." },
        { icon: "calculate",      titulo: "Decimal",              desc: "Resultado de dividir numerador entre denominador de Snellen. Se calcula automáticamente pero puede ajustarse manualmente." },
        { icon: "straighten",     titulo: "LogMAR",               desc: "Escala logarítmica de agudeza visual. 0.0 equivale a 20/20; valores positivos indican menor agudeza." },
      ],
      camposRefraccion: [
        { icon: "add_circle",     titulo: "Esfera",          desc: "Potencia esférica de la corrección en dioptrías (+ hipermetropía, − miopía). Paso de 0.25 D." },
        { icon: "circle",         titulo: "Cilindro",        desc: "Potencia cilíndrica para corregir el astigmatismo en dioptrías. Paso de 0.25 D." },
        { icon: "rotate_right",   titulo: "Eje (°)",         desc: "Orientación del cilindro en grados (0–180°)." },
        { icon: "add",            titulo: "Adición (ADD)",   desc: "Potencia adicional para visión de cerca en lentes bifocales o progresivos. Paso de 0.25 D." },
        { icon: "straighten",     titulo: "D.P. (mm)",       desc: "Distancia pupilar en milímetros para fabricar los lentes." },
        { icon: "eyeglasses",     titulo: "Tipo de lente",   desc: "Monofocal, bifocal, progresivo o lente de contacto." },
        { icon: "layers",         titulo: "Material",        desc: "Material del lente: CR-39, policarbonato, Trivex o alto índice." },
        { icon: "filter",         titulo: "Filtro / trat.",  desc: "Tratamiento adicional: antirreflejo, fotocromático, bloqueo de luz azul o combinación." },
      ],
      acciones: [
        {
          icon:   "add_circle",
          color:  "#0059cc",
          titulo: "Agregar registro",
          desc:   "Disponible en las pestañas Agudeza visual, Refracción, Biomicroscopía y Fondo/PIO. Agrega una nueva fila para registrar datos de un ojo adicional o una condición distinta.",
        },
        {
          icon:   "delete",
          color:  "#dc3545",
          titulo: "Eliminar registro",
          desc:   "Elimina la fila seleccionada de la lista. Solo visible en Modo Edición.",
        },
        {
          icon:   "restart_alt",
          color:  "#dc3545",
          titulo: "Limpiar",
          desc:   "Restablece todos los campos a sus valores iniciales y elimina todos los registros de las pestañas. Solo disponible en Modo Edición con atención PENDIENTE o PROCESO.",
        },
        {
          icon:   "save",
          color:  "#198754",
          titulo: "Guardar",
          desc:   "Persiste todos los datos de todas las pestañas en el servidor. Solo disponible en Modo Edición con atención PENDIENTE o PROCESO.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>